import { Capacitor, PermissionState, PluginListenerHandle } from '@capacitor/core';
import { NotificationSearchResultModel } from 'app/models/shared/notificationSearchResult.model';
import { Injectable } from '@angular/core';
import { NotificationResultModel } from 'app/models/shared/notificationResult.model';

import { UserService } from 'app/services/user.service';
import { AlertController, ToastController, Platform, NavController } from '@ionic/angular';
import { HttpClient, HttpParams } from '@angular/common/http';

import { CacheService } from './cache.service';
import { Constants } from 'app/utility/constants';
import { Observable, Subject } from 'rxjs';

import { FirebaseMessaging, GetTokenOptions, Notification } from '@capacitor-firebase/messaging';

import { NativeSettings } from 'capacitor-native-settings';

import { AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { Browser } from '@capacitor/browser';
import { environment } from 'environments/environment';
import { ServiceWorkerNotificationModel } from 'app/models/shared/serviceWorkerNotification.model';

@Injectable({
    providedIn: 'root'
})
export class NotificationService
{

    public notificationMessage = new Subject<number>();

    public isNative: boolean = Capacitor.isNativePlatform();
    public firebaseSwRegistered: boolean = false;

    private listenerHandles: PluginListenerHandle[] = [];
    private listenersInitialized = false;

    private swMessageListenerAdded = false;

    constructor(
        private httpClient: HttpClient,
        private cache: CacheService,
        private userService: UserService,
        private platform: Platform,
        private toastController: ToastController,
        private navCtrl: NavController,
        private alertController: AlertController
    )
    {

    }

    getNotifications(pageNumber: number = 1): Observable<NotificationSearchResultModel>
    {

        let httpParams = new HttpParams();
        httpParams = httpParams.set("pageNumber", pageNumber + "");
        httpParams = httpParams.set("pageSize", "10");
        // return this.httpClient.get<string[]>(Constants.ApiUrl + 'vehicles/index', { params: httpParams });
        return this.httpClient.get<NotificationSearchResultModel>(Constants.ApiUrl + 'Notifications', { params: httpParams });
    }

    getNotification(id: string): Observable<NotificationResultModel>
    {
        return this.httpClient.get<NotificationResultModel>(Constants.ApiUrl + 'Notifications/' + id);
    }

    getCount(): Observable<number>
    {
        return this.httpClient.get<number>(Constants.ApiUrl + 'Notifications/Count');
    }

    getCachedCount(): Observable<number>
    {
        let cacheKey: string = 'notifications';
        let cacheMilliseconds: number = 1 * 0.5 * 60000; //30 seconds
        return this.cache.get(cacheKey, this.getCount(), cacheMilliseconds);
    }

    public unreadNotificationCount: number;
    getLocalNotificationCount()
    {
        return this.unreadNotificationCount;
    }

    async setLocalNotificationCount(count: number)
    {
        this.unreadNotificationCount = count;
        this.doNotificationMessage();
    }

    doNotificationMessage()
    {
        this.notificationMessage.next(this.unreadNotificationCount);
    }

    reduceNotificationCount()
    {
        this.unreadNotificationCount--;
        this.doNotificationMessage();
    }

    increaseNotificationCount()
    {
        this.unreadNotificationCount++;
        this.doNotificationMessage();
    }

    readNotification(notification: NotificationResultModel): Observable<string>
    {
        this.reduceNotificationCount();
        return this.httpClient.get<string>(Constants.ApiUrl + 'Notifications/' + notification.notificationId);
    }

    unreadNotification(notification: NotificationResultModel): Observable<string>
    {
        this.increaseNotificationCount();
        return this.httpClient.patch<string>(Constants.ApiUrl + 'Notifications/' + notification.notificationId + '/mark-as-unread', '');
    }

    deleteNotification(notification: NotificationResultModel): Observable<string>
    {
        this.reduceNotificationCount();
        return this.httpClient.delete<string>(Constants.ApiUrl + 'Notifications/' + notification.notificationId);
    }

    deleteAllNotifications(): Observable<string>
    {
        this.setLocalNotificationCount(0);
        return this.httpClient.delete<string>(Constants.ApiUrl + 'Notifications/all');
    }

    readAllNotifications(): Observable<string>
    {
        this.setLocalNotificationCount(0);
        return this.httpClient.patch<string>(Constants.ApiUrl + 'Notifications/all', '');
    }


    currentRegisteredDeviceToken: string;
    private async callRegisterDeviceToken(token: string)
    {
        if (this.currentRegisteredDeviceToken && this.currentRegisteredDeviceToken === token)
        {
            console.debug("Token already registered ", token);
            return;
        }
        this.registerDeviceToken(token).subscribe({
            next: () =>
            {
                this.userService.setCurrentDeviceToken(token);
                console.debug("Token registered ", token);
                this.currentRegisteredDeviceToken = token;
            },
            error: (err) =>
            {
                console.warn('Device token registration failed', err);
            }
        });
    }

    registerDeviceToken(token: string): Observable<string>
    {
        var currentDeviceToken = this.userService.getCurrentDeviceToken();
        //we always want to register just in case...   // if (token!=currentDeviceToken) {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('deviceToken', token);
        if (currentDeviceToken != token)
        {
            httpParams = httpParams.append('deleteToken', currentDeviceToken);
        }

        return this.httpClient.post<string>(Constants.ApiUrl + 'Notifications/DeviceTokens', httpParams);
    }

    deRegisterDeviceToken(token: string): Observable<string>
    {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('deviceToken', token);
        return this.httpClient.delete<string>(Constants.ApiUrl + 'Notifications/DeviceTokens', { params: httpParams });
    }

    // Refer to https://capawesome.io/blog/the-push-notifications-guide-for-capacitor/
    // Most pages call this method with prompt to renable set to false because we don't want it to be obtrusive, however the notifications page sets this to true (it makes sense on the notifications page).
    async registerForPushNotifications(promptToRenable: boolean = false)
    {
        if (!this.isNative && !environment.serviceWorker)
        {
            console.debug('Service worker not available so not registering for push notifications');
            return;
        }

        try
        {
            let permissionResult = await FirebaseMessaging.checkPermissions();
            console.debug('FirebaseMessaging.checkPermissions', permissionResult);
            if (permissionResult.receive === 'prompt' || permissionResult.receive === 'prompt-with-rationale')
            {
                // if it's a pwa, we need to prompt the user for permission
                if (!this.isNative)
                {

                    if (!promptToRenable)
                    {
                        return;
                    }

                    console.debug('Prompting user for web push notification permissions');

                    //show alert controller asking user if they want to enable notifications
                    const alert = await this.alertController.create({
                        header: 'Register for alerts?',
                        message: 'Do you wish to enable web push notifications in order to receive updates and alerts from the app?',
                        buttons: [
                            {
                                text: 'Cancel',
                                role: 'cancel'
                            },
                            {
                                text: 'Enable',
                                handler: async () =>
                                {
                                    permissionResult = await FirebaseMessaging.requestPermissions();
                                    console.debug('FirebaseMessaging.requestPermissions', permissionResult);
                                    this.registerForPushNotificationsWithResult(permissionResult.receive, promptToRenable);
                                }
                            }
                        ]
                    });
                    await alert.present();
                }
                else
                {
                    permissionResult = await FirebaseMessaging.requestPermissions();
                    this.registerForPushNotificationsWithResult(permissionResult.receive, promptToRenable);
                }
            }
            else
            {
                // if we already have permission, register for push notifications
                this.registerForPushNotificationsWithResult(permissionResult.receive, promptToRenable);
            }
        }
        catch (error)
        {
            console.error('Error checking permissions:', error);
        }
    }

    async registerForPushNotificationsWithResult(receive: PermissionState, promptToRenable: boolean = false)
    {
        try
        {
            if (!this.isNative && !environment.serviceWorker)
            {
                console.debug('Service worker not available so not registering for push notifications');
                return;
            }

            if (receive !== 'granted')
            {
                if (promptToRenable)
                {
                    console.debug('Notifications are disabled, so showing toast');
                    this.showNotificationsDisabledToast();
                }
                return;
            }

            var vapidKey = environment.pwaFirebase?.vapidKey;
            let options: GetTokenOptions = { vapidKey };
            if (!this.isNative && !this.firebaseSwRegistered && environment.serviceWorker)
            {

                // Check if already registered
                let registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');

                if (!registration)
                {
                    registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                    this.firebaseSwRegistered = true;
                    console.debug('Firebase messaging SW registered:', registration);
                }

                if (registration)
                {
                    options.serviceWorkerRegistration = registration;
                }
            }

            const tokenResult = await FirebaseMessaging.getToken(options);
            if (tokenResult && tokenResult.token)
            {
                this.callRegisterDeviceToken(tokenResult.token);
            }

            await this.initWebPushListeners();

            await this.initPushListeners();

        }
        catch (error)
        {
            console.error('Error registering for push notifications:', error);
            // this.userService.handleError(error);
        }
    }

    private async initWebPushListeners()
    {
        if (this.isNative || !environment.serviceWorker)
        {
            return;
        }

        navigator.serviceWorker.addEventListener("message", (event: any) =>
        {
            //stringify the event data so i can ocpy it to the clipboard
            const eventDataString = JSON.stringify(event, null, 2);
            console.debug("Service Worker Message Data: ", eventDataString);

            console.debug("Service Worker Message: ", { event });
            const swNotification = event.data as ServiceWorkerNotificationModel;

            console.debug("Service Worker Notification: ", swNotification);
            this.presentToast(swNotification.notification.title, swNotification.data.id || '');

            // This is how we trigger a notification to the OS if we wanted to...
            // const notification = new Notification(
            //     swNotification.notification.title,
            //     {
            //         body: swNotification.notification.body,
            //         icon: swNotification.notification.image,
            //         data: swNotification
            //     }
            // );
            // notification.onclick = (event) =>
            // {
            //     this.navigateToNotification(swNotification.data.id || '');
            //     console.debug("notification clicked: ", { event });
            // };
        });
    }

    /**
     * Idempotent listener setup. Stores all plugin listener handles in an array.
     */
    private async initPushListeners()
    {
        if (!this.isNative) {
            return;
        }

        try
        {
            if (this.listenersInitialized) return;
            this.listenersInitialized = true;

            await FirebaseMessaging.addListener('tokenReceived', event =>
            {
                console.log('tokenReceived', { event });
            });

            this.listenerHandles.push(
                await FirebaseMessaging.addListener('notificationReceived', event =>
                {
                    console.log('notificationReceived', { event });
                    const notification = event.notification as any;
                    if (notification?.title)
                    {
                        this.presentToast(notification.title, notification?.data?.id || '');
                    }
                })
            );

            this.listenerHandles.push(
                await FirebaseMessaging.addListener('notificationActionPerformed', event =>
                {
                    console.log('notificationActionPerformed', { event });

                    // var notification = event.notification as PushNotificationSchema;

                    // var notificationId = (notification.data.id) ? notification.data.id : "";

                    // if (notificationId)
                    // {
                    //     this.navigateToNotification(notificationId);
                    // }
                })
            );

            //Other events:
            // await FirebaseMessaging.removeAllListeners();
            // await FirebaseMessaging.deleteToken();
            // const result = await FirebaseMessaging.getDeliveredNotifications();
            // await FirebaseMessaging.removeDeliveredNotifications
            // FirebaseMessaging.removeAllDeliveredNotifications();
            // FirebaseMessaging.subscribeToTopic({ topic: 'news' });
            // FirebaseMessaging.unsubscribeFromTopic({ topic: 'news' });
        }
        catch (error)
        {
            console.error('Error registering for push notifications:', error);
            // this.userService.handleError(error);
        }
    }

    async presentToast(title, notificationId: string)
    {
        const toast = await this.toastController.create({
            header: "Notification Received",
            message: title,
            duration: 0,
            position: 'top',
            buttons: [
                {
                    // side: 'start',
                    text: 'View',
                    handler: () =>
                    {
                        this.navigateToNotification(notificationId);
                        toast.dismiss();
                    }
                }, {
                    text: 'Close',
                    role: 'cancel',
                    handler: () =>
                    {
                        toast.dismiss();
                    }
                }
            ],
            layout: "stacked"
        });
        toast.present();
    }

    private navigateToNotification(notificationId: string)
    {
        console.debug("Navigating to notification with ID: ", notificationId);
        this.navCtrl.navigateRoot("/notifications/" + notificationId);
    }

    public handleError(error: any, backEnabled: boolean = true): void
    {
        if (error.status && error.StatusCode == "404")
        {
            this.userService.presentAlert("Not Found Error", "Notification not found. If you share a login with other users the notification may have already been deleted by one of those users. If you log into multiple Motorcentral accounts on this device, the notification may relate to one of your other accounts. In this case, you can logout of this account and then log in to the correct account to view the details of this notification");
            return;
        }
        this.userService.handleError(error);
    }

    async openNotificationSettings()
    {
        if (!this.isNative)
        {
            Browser.open({ url: 'https://support.motorcentral.co.nz/hc/en-us/articles/13195885653775' });
            return;
        }

        try
        {
            NativeSettings.open({
                optionAndroid: AndroidSettings.ApplicationDetails,
                optionIOS: IOSSettings.App
            });
        } catch (error)
        {
            console.error('Error opening settings:', error);
            // Handle the case when the device doesn't support opening settings directly
        }
    }

    async showNotificationsDisabledToast()
    {
        var message = `Push notifications are disabled. To enable them you will need to update your ${this.isNative ? 'app' : 'browser'} settings.`;
        var buttonCaption = this.isNative ? 'Open settings' : 'Learn how';

        const toast = await this.toastController.create({
            // header: "Push notifications disabled",
            message,
            position: 'bottom',
            buttons: [
                {
                    text: buttonCaption,
                    handler: () =>
                    {
                        this.openNotificationSettings();
                        toast.dismiss();
                    }
                }
                , {
                    text: 'Close',
                    role: 'cancel',
                    handler: () =>
                    {
                        toast.dismiss();
                    }
                }
            ],
            layout: "stacked"
        });
        toast.present();

    }
}
